
import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  CircularProgress
} from '@mui/material';
import { useDispatch } from 'react-redux';
import { AppDispatch } from '../../../../store';
import { getInterviewDetail } from '../../../../modules/interview';
import apiCall from '../../../../utils/api';

interface UploadRecordingModalProps {
  open: boolean;
  onClose: () => void;
  interviewId: number;
  workerId: number;
}

const UploadRecordingModal: React.FC<UploadRecordingModalProps> = ({
  open,
  onClose,
  interviewId,
  workerId,
}) => {
  const dispatch: AppDispatch = useDispatch();
  const [file, setFile] = useState<File | null>(null);
  const [remark, setRemark] = useState('');
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files.length > 0) {
      setFile(event.target.files[0]);
      setError(null);
    }
  };

  const handleUpload = async () => {
    if (!file) {
      setError('Please select a file to upload.');
      return;
    }

    setUploading(true);
    setProgress(0);
    setError(null);

    const formData = new FormData();
    formData.append('file', file);
    formData.append('remark', remark);

    try {
      await apiCall(
        'post',
        `/api/interview/upload-recording/${interviewId}/`,
        formData,
        {}, // extraHeaders - let apiCall handle Content-Type removal for FormData
        undefined, // responseType
        (progressEvent) => {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setProgress(percentCompleted);
        }
      );

      dispatch(getInterviewDetail(workerId) as any);
      onClose();
      // Reset state
      setFile(null);
      setRemark('');
      setProgress(0);

    } catch (err: any) {
      console.error(err);
      setError(err.response?.data?.message || 'An unexpected error occurred during upload.');
    } finally {
      setUploading(false);
    }
  };

  const handleClose = () => {
    if (!uploading) {
      onClose();
      setFile(null);
      setRemark('');
      setError(null);
      setProgress(0);
    }
  }


  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Upload Interview Recording</DialogTitle>
      <DialogContent>
        <Box mt={2} mb={2}>
          <input
            accept="video/*"
            style={{ display: 'none' }}
            id="raised-button-file"
            type="file"
            onChange={handleFileChange}
          />
          <label htmlFor="raised-button-file">
            <Button variant="outlined" component="span" fullWidth>
              {file ? file.name : 'Choose Video File'}
            </Button>
          </label>
        </Box>

        <TextField
          label="Remark / Feedback"
          multiline
          rows={4}
          variant="outlined"
          fullWidth
          value={remark}
          onChange={(e) => setRemark(e.target.value)}
          placeholder="Enter detailed feedback or notes about the interview..."
        />

        {error && (
          <Box mt={2}>
            <Typography color="error" variant="body2">
              {error}
            </Typography>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} color="primary" disabled={uploading}>
          Cancel
        </Button>
        <Button
          onClick={handleUpload}
          color="primary"
          variant="contained"
          disabled={!file || uploading}
        >
          {uploading ? (
            <Box display="flex" alignItems="center">
              <CircularProgress size={24} color="inherit" sx={{ mr: 1 }} />
              {progress}%
            </Box>
          ) : 'Upload'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default UploadRecordingModal;
